import type { ObservableCollection } from '@acies/observables';
import type DropdownItem from './dropdown-item';
import { Container, SelectListEvent } from '@acies/core';
import DropdownEvent from './dropdown-event';
import DropdownItemRenderer from './dropdown-item-renderer';
import DropdownList from './dropdown-list';
import Icon from './icon';
import { ICONS } from './icons';
import Label from './label';
import { placePopover, type PopoverPlacement, setPopoverMaxHeight } from './popover-placement';
import StateContainer from './state-container';

/**
 * The Dropdown component acts exactly like a <select/> element, but is styled according to the Design System.
 *
 * When an DropdownItem is selected, a DropdownEvent will be dispatched with a value property, that is a reference to the DropdownItem value property.
 *
 * If the user de-selects a DropdownItem, the DropdownEvent value will be null.
 */
export default class Dropdown extends StateContainer {
	public static override observedAttributes: string[] = [...StateContainer.observedAttributes, 'icon', 'placeholder'];
	/** The current random generated anchorName, that is used internally by the popover <div/>, so it knows how to position itself and open / close correctly */
	public get anchorName(): string {
		return this.#anchorName;
	}

	/**
	 * Picks the first DropdownItem on offer whenever nothing is picked, so the Dropdown is never empty.
	 *
	 * False by default, so a Dropdown keeps showing its placeholder until the user chooses. Set it to true where a choice is always required and the first item is the sensible one.
	 *
	 * It applies when the data is first rendered and every time the data is replaced, since a replacement drops whatever was picked. A DropdownEvent is dispatched for the pick it makes, exactly as for one the user makes, so a listener has to be in place before the Dropdown is added to the DOM to hear the first one.
	 */
	public set autoSelectFirstItem(value: boolean) {
		if (this.#autoSelectFirstItem === value) {
			return;
		}
		assertIsBoolean(value);
		this.#autoSelectFirstItem = value;
	}

	public get autoSelectFirstItem(): boolean {
		return this.#autoSelectFirstItem;
	}

	/**
	 * The container holds the icon and the label child, so they stay together to the left, while the chevron child stays to the right.
	 *
	 * The icon child is the first child and the label child is the second child.
	 */
	public get container(): Container {
		return this.#container;
	}

	/**
	 * The data property is the data source for the DropdownList.
	 *
	 * You can pass null, that will clear the List.
	 *
	 * @param value An Array or an ObservableCollection as the data item set to render individualy.
	 */
	public set data(value: DropdownItem[] | null | ObservableCollection<DropdownItem>) {
		this.#listChild.data = value;
	}

	public get data(): DropdownItem[] | null | ObservableCollection<DropdownItem> {
		return this.#listChild.data;
	}

	/**
	 * The icon is shown to the left and is helpful if you want to add further visual information.
	 *
	 * If the selected DropdownItem has an icon, that icon is shown instead, in the same way the selected DropdownItem text is shown instead of the placeholder text.
	 *
	 * @param value The name of the icon you want to show.
	 */
	public set icon(value: keyof typeof ICONS | null) {
		if (this.#icon === value) {
			return;
		}
		assertIsValidIconName(value);
		this.#icon = value;
		this.#iconChanged = true;
		this.invalidateProperties();
	}

	public get icon(): keyof typeof ICONS | null {
		return this.#icon;
	}

	/**
	 * The placeholder property defines the text displayed in the Dropdown if no Item is selected.
	 *
	 * When the user selects an Item from the Dropdown List with a Pointer or Keyboard, the Item text value is displayed.
	 *
	 * When the user, de-selects an Item in the List, the placeholder text is displayed.
	 */
	public set placeholder(value: string) {
		if (this.#placeholder === value) {
			return;
		}
		assertIsString(value);
		this.#placeholder = value;
		this.#placeholderChanged = true;
		this.invalidateProperties();
	}

	public get placeholder(): string {
		return this.#placeholder;
	}

	/**
	 * The side of the Dropdown the popover opens on, when there is room for it there.
	 *
	 * "bottom" by default. Where there is no room, the browser moves the popover to the opposite side, then, for "left" and "right", below or above. Should there be room for all of it nowhere, it shrinks to fit below or above, where there is the most room.
	 */
	public set popoverPlacement(value: PopoverPlacement) {
		if (this.#popoverPlacement === value) {
			return;
		}
		assertIsPopoverPlacement(value);
		this.#popoverPlacement = value;
		this.#popoverPlacementChanged = true;
		this.invalidateProperties();
	}

	public get popoverPlacement(): PopoverPlacement {
		return this.#popoverPlacement;
	}

	/**
	 * How wide the popover is, in pixels, or null for as wide as the Dropdown.
	 *
	 * Null by default, so the list lines up under the Dropdown it opens from. Set it where the items say more than the Dropdown has room to show: the popover keeps its left edge on the Dropdown's and grows to the right, or, where there is no room for that, keeps its right edge on the Dropdown's and grows to the left.
	 */
	public set popoverWidth(value: number | null) {
		if (this.#popoverWidth === value) {
			return;
		}
		assertIsPopoverWidth(value);
		this.#popoverWidth = value;
		this.#popoverWidthChanged = true;
		this.invalidateProperties();
	}

	public get popoverWidth(): number | null {
		return this.#popoverWidth;
	}

	/** This readonly property is actually used for state testing and has no outside value. */
	public get popoverState(): 'closed' | 'open' {
		return this.#popoverState;
	}

	/**
	 * Picks one of the DropdownItems on offer, or null to pick none.
	 *
	 * The item has to be one of the instances in data: it is found by identity, the way the list keeps its selection, so an equal DropdownItem made elsewhere picks nothing and leaves the selection as it was.
	 *
	 * A DropdownEvent is dispatched for the pick, exactly as for one the user makes, so a listener that only wants to hear the user has to tell the two apart itself. The popover stays closed: the pick is made by clicking the item renderer, the way autoSelectFirstItem makes its own, and that click is not the user's.
	 *
	 * Set before the Dropdown is in the DOM, the pick is made once it is, and it takes the place of the one autoSelectFirstItem would have made.
	 */
	public set selectedItem(value: DropdownItem | null) {
		const current = this.#selectedItemRequested ? this.#pendingSelectedItem : this.#selectedItem;
		if (current === value) {
			return;
		}
		this.#pendingSelectedItem = value;
		this.#selectedItemRequested = true;
		this.invalidateProperties();
	}

	/** Returns the current selected DropdownItem instance or null if nothing is selected. */
	public get selectedItem(): DropdownItem | null {
		return this.#selectedItem;
	}

	#anchorName: string;
	#autoSelectFirstItem: boolean;
	#chevronChild: Icon;
	#container: Container;
	#icon: keyof typeof ICONS | null;
	#iconChanged: boolean;
	#iconChild: Icon;
	#labelChild: Label;
	#listChild: DropdownList;
	#pendingSelectedItem: DropdownItem | null;
	#placeholder: string;
	#placeholderChanged: boolean;
	#popoverChild!: HTMLDivElement;
	#popoverPlacement: PopoverPlacement;
	#popoverPlacementChanged: boolean;
	#popoverState: 'closed' | 'open';
	#popoverWidth: number | null;
	#popoverWidthChanged: boolean;
	#selectedItem: DropdownItem | null;
	#selectedItemRequested: boolean;
	#selectingFromCode: boolean;

	public constructor() {
		super();
		this.alignment = 'left';
		this.gapHorizontal = 10;
		this.minWidth = 240;
		this.paddingHorizontal = 10;
		// scope the listener to this since we are listening on the #popoverChild instance
		this.toogle = this.toogle.bind(this);
		// we bind the keyboardListener, since we are listening in the window scope.
		// we can't bind #private methods, so we do it typesafe instead.
		this.arrowDownListener = this.arrowDownListener.bind(this);
		// we randomize the anchorName since multiple popovers wouldn't know
		// what "anchor" to position itself against
		// Math.random() didn't work directly, maybe the punctuation, so we just create an integer value
		this.#anchorName = `--${Math.round(Math.random() * 1000000 + 1)}`;
		this.#chevronChild = new Icon();
		this.#chevronChild.name = 'chevron-down';
		this.#container = new Container();
		this.#container.alignment = 'left';
		this.#container.gapHorizontal = 10;
		this.#container.width = 'fill';
		this.#icon = null;
		this.#iconChanged = false;
		this.#iconChild = new Icon();
		this.#iconChild.visible = false;
		this.#labelChild = new Label();
		this.#labelChild.width = 'fill';
		this.#listChild = new DropdownList();
		this.#placeholder = '';
		this.#placeholderChanged = false;
		this.#autoSelectFirstItem = false;
		this.#pendingSelectedItem = null;
		this.#popoverPlacement = 'bottom';
		this.#popoverPlacementChanged = false;
		this.#popoverState = 'closed';
		this.#popoverWidth = null;
		this.#popoverWidthChanged = false;
		this.#selectedItem = null;
		this.#selectedItemRequested = false;
		this.#selectingFromCode = false;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'placeholder') {
			this.#placeholderAttributeChanged(value);
		} else if (name === 'icon') {
			this.#iconAttributeChanged(value);
		}
	}

	protected override clicked(): void {
		// a click this component made on its own item renderer bubbles right back up to here, and
		// nothing about it says who made it. Opening the popover for it would show a list the user
		// never asked to see.
		if (this.#selectingFromCode) {
			return;
		}
		if (this.#popoverState === 'open') {
			this.#popoverChild.hidePopover();
		} else {
			this.#popoverChild.showPopover();
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#iconChanged) {
			this.#commitIcon();
		}
		if (this.#placeholderChanged) {
			this.#commitPlaceholder();
		}
		if (this.#popoverPlacementChanged) {
			this.#commitPopoverPlacement();
		}
		if (this.#popoverWidthChanged) {
			this.#commitPopoverWidth();
		}
		if (this.#selectedItemRequested) {
			this.#commitSelectedItem();
		}
	}

	protected override enabledChanged(): void {
		super.enabledChanged();
		this.#updateChildrenVariantsFromEnabledProperty();
	}

	protected override initialize(): void {
		super.initialize();
		this.style.anchorName = this.#anchorName;
		this.#container.addComponents([this.#iconChild, this.#labelChild]);
		this.addComponents([this.#container, this.#chevronChild]);

		// we don't use a Container component but a plain <div/> as popover container,
		// since the Container component has a lot of default styles that messes with
		// the popover API, for example the display style, so keep it simple so the
		// popover API can do it's thing correctly
		this.#popoverChild = document.createElement('div');
		this.#popoverChild.appendChild(this.#listChild);
		this.appendChild(this.#popoverChild);
		this.#setPopoverDefaultProperties();
		this.#updateLabel();
		this.addEventListener(SelectListEvent.SELECTION_CHANGED, this.#selectedItemChanged as EventListener);
		// the list rendered its items as it was appended above, so there is one to pick by now. This
		// first pick cannot come through #selectedItemChanged: the list reported its data long
		// before the listener above existed.
		this.#autoSelectFirstItemIfNoneSelected();
	}

	#addArrowDownListener(): void {
		window.addEventListener('keyup', this.arrowDownListener);
	}

	#commitIcon(): void {
		this.#iconChanged = false;
		this.#updateIcon();
	}

	#commitPlaceholder(): void {
		this.#placeholderChanged = false;
		this.#updateLabel();
	}

	#commitPopoverPlacement(): void {
		this.#popoverPlacementChanged = false;
		this.#updatePopoverPlacement();
	}

	#commitPopoverWidth(): void {
		this.#popoverWidthChanged = false;
		this.#updatePopoverWidth();
	}

	/**
	 * Makes the pick the selectedItem setter asked for.
	 *
	 * By clicking, for the reason given in #autoSelectFirstItemIfNoneSelected. The click toggles, so picking none is a click on the item renderer holding the current pick, and picking what is already picked is left alone.
	 */
	#commitSelectedItem(): void {
		this.#selectedItemRequested = false;
		const target = this.#pendingSelectedItem;
		this.#pendingSelectedItem = null;
		if (target === this.#selectedItem) {
			return;
		}
		const itemRenderer = this.#itemRendererFor(target ?? this.#selectedItem);
		if (itemRenderer) {
			this.#clickFromCode(itemRenderer);
		}
	}

	/** Clicks an item renderer on the Dropdown's own behalf. The flag is what tells clicked() the click is not the user's, and it is safe because dispatching a click is synchronous. */
	#clickFromCode(itemRenderer: DropdownItemRenderer): void {
		this.#selectingFromCode = true;
		itemRenderer.click();
		this.#selectingFromCode = false;
	}

	#dispatchDropdownEvent(event: SelectListEvent<DropdownItem>): void {
		if (event.selectedItem) {
			this.dispatchEvent(new DropdownEvent(event.selectedItem.value));
		} else {
			this.dispatchEvent(new DropdownEvent());
		}
	}

	#focusElementWithSelectedItem(): void {
		for (const child of this.#listChild.children) {
			const element = this.#getItemRendererWithItemAsSelected(child);
			if (element) {
				element.focus();
			}
		}
	}

	#focusFirstDropdownRenderer(): void {
		const firstElement = this.#listChild.children.item(0);
		if (firstElement && firstElement instanceof DropdownItemRenderer) {
			firstElement.focus();
		}
	}

	#focusFirstOrSelectedDropdownItem(): void {
		if (this.#selectedItem) {
			this.#focusElementWithSelectedItem();
		} else {
			this.#focusFirstDropdownRenderer();
		}
	}

	#getItemRendererWithItemAsSelected(element: Element): DropdownItemRenderer | null {
		if (element instanceof DropdownItemRenderer && element.item === this.#selectedItem) {
			return element;
		}
		return null;
	}

	#itemRendererFor(item: DropdownItem | null): DropdownItemRenderer | null {
		for (const child of this.#listChild.children) {
			if (child instanceof DropdownItemRenderer && child.item === item) {
				return child;
			}
		}
		return null;
	}

	#iconAttributeChanged(value: string): void {
		this.icon = value as keyof typeof ICONS | null;
	}

	#placeholderAttributeChanged(value: string): void {
		this.placeholder = value;
	}

	#removeArrowDownListener(): void {
		window.removeEventListener('keyup', this.arrowDownListener);
	}

	/**
	 * Picks the first item on offer, if there is one and nothing is picked.
	 *
	 * The item renderer is clicked rather than the list's selectedItem set, which is how SelectList makes a selection itself: the list records a click where it reports selections from, while its setter also writes a field it guards itself against.
	 */
	#autoSelectFirstItemIfNoneSelected(): void {
		// a pick asked for through selectedItem is still to be made, and it is the one that counts
		if (!this.#autoSelectFirstItem || this.#selectedItem !== null || this.#selectedItemRequested) {
			return;
		}
		const firstElement = this.#listChild.children.item(0);
		if (firstElement instanceof DropdownItemRenderer) {
			// clicking is the only way to make a selection the list hears: setting an item
			// renderer's selected property styles it, but SelectableContainer only dispatches its
			// SelectEvent from an actual click
			this.#clickFromCode(firstElement);
		}
	}

	#selectedItemChanged(event: SelectListEvent<DropdownItem>): void {
		event.stopImmediatePropagation();
		this.#selectedItem = event.selectedItem;
		this.#popoverChild.hidePopover();
		this.#updateIcon();
		this.#updateLabel();
		this.#dispatchDropdownEvent(event);
		// last, so the Dropdown has finished reporting that nothing is picked before it picks
		// again. Replacing the data is what gets here with nothing selected.
		this.#autoSelectFirstItemIfNoneSelected();
	}

	#setPopoverDefaultProperties(): void {
		// prevent the default black border to show up
		this.#popoverChild.style.border = 'none';
		this.#popoverChild.style.borderRadius = '4px';
		this.#popoverChild.style.boxShadow = 'rgba(51, 51, 51, 0.08) 0px 5px 5px -2.5px, rgba(51, 51, 51, 0.16) 0px 1px 3px -1.5px';
		// we select a maxHeight that will "cut" the last viewable Dropdown Item Renderer in half,
		// so it's easy to see that there are more items in the list if we scroll or key down
		setPopoverMaxHeight(this.#popoverChild, 204);
		this.#popoverChild.style.minHeight = '32px';
		this.#popoverChild.style.outline = 'rgb(225, 228, 234) solid 1px';
		this.#popoverChild.style.padding = '0px';
		// the popover property enables the popover functionality,
		// "auto" enables closing with ESC key and click outside
		this.#popoverChild.popover = 'auto';
		this.#popoverChild.style.positionAnchor = this.#anchorName;
		// hide the scrollbars
		this.#popoverChild.style.scrollbarWidth = 'none';
		// 4px from the side of the Dropdown popoverPlacement prefers, or another side with more room
		this.#updatePopoverPlacement();
		// as wide as the dropdown, unless popoverWidth says otherwise
		this.#updatePopoverWidth();
		// add state listener, so we get notified if the user clicks outside or presses the ESC key
		this.#popoverChild.addEventListener('toggle', this.toogle as EventListener);
	}

	/**
	 * Instead of doing rotation, we can just swap the icon name at runtime.
	 *
	 * If we want animation at a later stage, we can use rotation and set the origin point in the middle of the icon instance.
	 */
	#updateChevron(): void {
		if (this.#popoverState === 'closed') {
			this.#chevronChild.name = 'chevron-down';
		} else {
			this.#chevronChild.name = 'chevron-up';
		}
	}

	#updateChildrenVariantsFromEnabledProperty(): void {
		if (this.enabled) {
			this.#iconChild.variant = 'default';
			this.#labelChild.variant = 'default';
			this.#chevronChild.variant = 'default';
		} else {
			this.#iconChild.variant = 'disabled';
			this.#labelChild.variant = 'disabled';
			this.#chevronChild.variant = 'disabled';
		}
	}

	/**
	 * The icon of the selected DropdownItem takes precedence over the Dropdown icon property, so the closed Dropdown shows the same icon as the selected DropdownItemRenderer in the List.
	 *
	 * If nothing is selected, or the selected DropdownItem has no icon, we fall back to the Dropdown icon property.
	 */
	#updateIcon(): void {
		if (this.#selectedItem && this.#selectedItem.icon) {
			this.#iconChild.name = this.#selectedItem.icon;
		} else {
			this.#iconChild.name = this.icon;
		}
		this.#iconChild.visible = this.#iconChild.name !== null;
	}

	#updatePopoverPlacement(): void {
		placePopover(this.#popoverChild, this.#popoverPlacement);
	}

	/** Sizes the popover to popoverWidth, or to the Dropdown's own width while that is null. */
	#updatePopoverWidth(): void {
		this.#popoverChild.style.width = this.#popoverWidth === null ? 'anchor-size(width)' : `${this.#popoverWidth}px`;
	}

	#updateKeyboardListener(): void {
		if (this.#popoverState === 'open') {
			this.#addArrowDownListener();
		} else {
			this.#removeArrowDownListener();
		}
	}

	/**
	 * The text of the selected DropdownItem takes precedence over the placeholder text.
	 *
	 * If nothing is selected, or the selected DropdownItem has no text, we fall back to the placeholder text.
	 *
	 * The label child is only shown when we end up with a text.
	 */
	#updateLabel(): void {
		if (this.#selectedItem && this.#selectedItem.text) {
			this.#labelChild.text = this.#selectedItem.text;
		} else {
			this.#labelChild.text = this.placeholder;
		}
		this.#labelChild.visible = this.#labelChild.text !== '';
	}

	private arrowDownListener(event: KeyboardEvent): void {
		if (event.code === 'ArrowDown') {
			this.#focusFirstOrSelectedDropdownItem();
			this.#removeArrowDownListener();
		}
	}

	private toogle(event: ToggleEvent): void {
		this.#popoverState = event.newState as 'closed' | 'open';
		this.#updateChevron();
		this.#updateKeyboardListener();
	}
}
customElements.define('ac-dropdown', Dropdown);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be true or false`);
}

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}

function assertIsPopoverPlacement(value: unknown): asserts value is PopoverPlacement {
	if (value === 'bottom' || value === 'left' || value === 'right' || value === 'top') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "bottom", "left", "right" or "top"`);
}

function assertIsPopoverWidth(value: unknown): asserts value is number | null {
	if ((typeof value === 'number' && Number.isFinite(value) && value >= 0) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a number of 0 or more, or null`);
}

function assertIsValidIconName(value: unknown): asserts value is keyof typeof ICONS | null {
	if ((typeof value === 'string' && value in ICONS) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string and in ICONS object or null`);
}
